"use client";
import { useState } from 'react';
import * as LucideIcons from 'lucide-react';
import * as FaIcons from 'react-icons/fa6';
const Icons = { ...LucideIcons, ...FaIcons };
import Link from 'next/link';
import { componentDefaults } from "./data";
import { createUpdateHandler } from "./component-helpers";
import styles from "./header-section.module.css";


const openDialog = (id) => {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('lunar:open-dialog', { detail: { id } }));
  }
};

const BuilderSection = ({ tagName = 'div', className, innerContainer, fullWidth, style, children, id, sectionId, isVisible = true, removePaddingLeft, removePaddingRight, onUpdate, ...rest }) => {
  if (!isVisible) return null;
  const Tag = tagName;
  const normalizedSectionId = (sectionId && typeof sectionId === 'string') ? sectionId.replace(/-+$/, '') : '';
  let finalId = id || normalizedSectionId;
  finalId = finalId ? finalId.replace(/-+/g, '-') : undefined;
  const containerClasses = ["container-grid"];
  if (removePaddingLeft === true || removePaddingLeft === "true") containerClasses.push("pl-0");
  if (removePaddingRight === true || removePaddingRight === "true") containerClasses.push("pr-0");
  if (fullWidth === true || fullWidth === "true") containerClasses.push("container-full");
  const containerClass = containerClasses.join(" ");
  if (innerContainer) {
    return (
      <Tag id={finalId} className={className} style={style}>
        <div className={containerClass}>{children}</div>
      </Tag>
    );
  }
  return <Tag id={finalId} className={containerClass + " " + (className || '')} style={style}>{children}</Tag>;
};

const BuilderText = ({ tagName = 'p', content, className, style, children, id, sectionId, suffix, isVisible = true, tooltipIfTruncated, onUpdate, ...rest }) => {
  if (!isVisible) return null;
  const Tag = tagName;
  const normalizedSectionId = (sectionId && typeof sectionId === 'string') ? sectionId.replace(/-+$/, '') : '';
  const effectiveSuffix = suffix || (className ? className.split(' ')[0] : tagName);
  let finalId = id || (normalizedSectionId ? normalizedSectionId + '-' + effectiveSuffix : undefined);
  finalId = finalId ? finalId.replace(/-+/g, '-') : undefined;
  const finalClassName = ("builder-text " + (className || '') + " " + (!content && !children ? 'empty-builder-text' : '')).trim();
  const title = tooltipIfTruncated ? content : undefined;
  const isSimpleString = content && typeof content === 'string' && !/<[a-z]|&[a-z0-9#]+;/i.test(content);
  return (
    <Tag
      id={finalId}
      className={finalClassName}
      style={style}
      dangerouslySetInnerHTML={content && !isSimpleString ? { __html: content } : undefined}
      title={title}
    >
      {isSimpleString ? content : (!content ? children : null)}
    </Tag>
  );
};

const BuilderButton = ({ label, href, openInNewTab, className, style, children, linkType, targetDialogId, id, sectionId, suffix, iconLeft, iconRight, hideLabel, isVisible = true, onUpdate, isSubmit, disabled, isLoading, onClick, disableLinkSettings, svgPath, svgViewBox, svgStrokeWidth, ...rest }) => {
  if (!isVisible) return null;
  const normalizedSectionId = (sectionId && typeof sectionId === 'string') ? sectionId.replace(/-+$/, '') : '';
  let finalId = id || (normalizedSectionId && suffix ? normalizedSectionId + '-' + suffix : undefined);
  finalId = finalId ? finalId.replace(/-+/g, '-') : undefined;
  const renderIcon = (icon) => {
    if (typeof icon === 'string' && typeof Icons !== 'undefined' && Icons[icon]) {
      const IconComponent = Icons[icon];
      return <IconComponent style={{ width: '1.25rem', height: '1.25rem' }} />;
    }
    return typeof icon === 'string' ? null : icon;
  };
  const isSimpleLabel = label && typeof label === 'string' && !/<[a-z]|&[a-z0-9#]+;/i.test(label);
  const hasSvgBorder = !!(svgPath && typeof svgPath === 'string' && svgPath.trim());
  const content = (
      <>
        {hasSvgBorder && (
          <svg
            viewBox={svgViewBox || "0 0 158 57"}
            preserveAspectRatio="none"
            aria-hidden="true"
            style={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              pointerEvents: 'none',
              overflow: 'visible',
              zIndex: 0
            }}
          >
            <path
              d={svgPath}
              className="btn-svg-border"
              stroke="none"
              style={{
                stroke: 'none',
                strokeWidth: 0,
                filter: 'drop-shadow(var(--shadow-x-button, 0px) var(--shadow-y-button, 0px) var(--shadow-blur-button, 0px) var(--shadow-color-button, transparent))'
              }}
            />
            <path
              className="btn-svg-border"
              d={svgPath}
              vectorEffect="non-scaling-stroke"
              style={svgStrokeWidth ? { strokeWidth: svgStrokeWidth } : undefined}
            />
          </svg>
        )}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%', gap: 'inherit', position: 'relative', zIndex: 1, opacity: isLoading ? 0.5 : 1 }}>
           {isLoading && <div className="spinner-sm" style={{ marginRight: '8px' }}></div>}
           {!isLoading && renderIcon(iconLeft) && <span style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>{renderIcon(iconLeft)}</span>}
            {!hideLabel && (
               <div style={{ minWidth: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {isSimpleLabel ? label : (label && typeof label === 'string' ? <span dangerouslySetInnerHTML={{ __html: label }} /> : (label || children))}
               </div>
            )}
           {!isLoading && renderIcon(iconRight) && <span style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>{renderIcon(iconRight)}</span>}
        </div>
      </>
  );
  const baseClass = className ? (className.split(' ').includes('btn') ? className : ('btn ' + className)) : 'btn';
  const finalClassName = hasSvgBorder ? (baseClass + ' has-svg-border') : baseClass;
  const isUrlLink = !isSubmit && linkType !== 'dialog' && !!href;
  const svgStyleOverrides = hasSvgBorder ? { background: 'transparent', border: 'none', boxShadow: 'none', position: 'relative' } : {};
  const buttonStyle = { ...style, textDecoration: 'none', ...svgStyleOverrides };
  if (isSubmit) {
    return <button id={finalId} type="submit" disabled={disabled || isLoading} className={finalClassName} style={buttonStyle} onClick={onClick}>{content}</button>;
  }
  if (linkType === 'dialog' && targetDialogId) {
    return (
      <button id={finalId} type="button" disabled={disabled || isLoading} className={finalClassName} style={{ ...buttonStyle, cursor: 'pointer' }} onClick={(e) => { if (onClick) onClick(e); openDialog(targetDialogId); }}>{content}</button>
    );
  }
  if (!isUrlLink) {
    return <button id={finalId} type="button" disabled={disabled || isLoading} className={finalClassName} style={buttonStyle} onClick={onClick}>{content}</button>;
  }
  const isNewTab = openInNewTab === true || openInNewTab === 'true';
  const targetAttr = isNewTab ? '_blank' : undefined;
  const relAttr = isNewTab ? 'noopener noreferrer' : 'noopener';

  return <a id={finalId} href={href} target={targetAttr} className={finalClassName} style={buttonStyle} rel={relAttr} onClick={onClick}>{content}</a>;
};
export default function HeaderSection({
title = componentDefaults["header-section"].title,
titleTypography = componentDefaults["header-section"].titleTypography,
subtitle = componentDefaults["header-section"].subtitle,
subtitleTypography = componentDefaults["header-section"].subtitleTypography,
sectionId,
buttonText = componentDefaults["header-section"].buttonText,
buttonUrl = componentDefaults["header-section"].buttonUrl,
buttonOpenInNewTab = componentDefaults["header-section"].buttonOpenInNewTab || false,
buttonVisible = componentDefaults["header-section"].buttonVisible,
buttonLinkType = componentDefaults["header-section"].buttonLinkType || "url",
buttonTargetDialogId = componentDefaults["header-section"].buttonTargetDialogId,
buttonIconLeft = componentDefaults["header-section"].buttonIconLeft,
buttonIconRight = componentDefaults["header-section"].buttonIconRight,
buttonSvgPath = componentDefaults["header-section"].buttonSvgPath,
buttonSvgViewBox = componentDefaults["header-section"].buttonSvgViewBox,
buttonSvgStrokeWidth = componentDefaults["header-section"].buttonSvgStrokeWidth,
secondaryButtonText = componentDefaults["header-section"].secondaryButtonText,
secondaryButtonUrl = componentDefaults["header-section"].secondaryButtonUrl,
secondaryButtonOpenInNewTab = componentDefaults["header-section"].secondaryButtonOpenInNewTab || false,
secondaryButtonVisible = componentDefaults["header-section"].secondaryButtonVisible,
secondaryButtonLinkType = componentDefaults["header-section"].secondaryButtonLinkType || "url",
secondaryButtonTargetDialogId = componentDefaults["header-section"].secondaryButtonTargetDialogId,
secondaryButtonIconLeft = componentDefaults["header-section"].secondaryButtonIconLeft,
secondaryButtonIconRight = componentDefaults["header-section"].secondaryButtonIconRight,
secondaryButtonSvgPath = componentDefaults["header-section"].secondaryButtonSvgPath,
secondaryButtonSvgViewBox = componentDefaults["header-section"].secondaryButtonSvgViewBox,
secondaryButtonSvgStrokeWidth = componentDefaults["header-section"].secondaryButtonSvgStrokeWidth,
buttonId,
secondaryButtonId,
buttonStyle = "primary",
secondaryButtonStyle = "ghost",
onUpdate,
titleVisible = true,
subtitleVisible = true,
titleId,
subtitleId
}) {
const update = createUpdateHandler(onUpdate);
return (
<BuilderSection
tagName="section"
className={`${styles.section}`}
sectionId={sectionId}
showTypographyToggle={true}
titleTypography={titleTypography}
subtitleTypography={subtitleTypography}
showFullWidthControl={false}
innerContainer={true}
>
<div className="grid">
<div className={`col-mobile-4 col-tablet-8 col-desktop-8 offset-desktop-2 ${styles.content}`}>
{titleVisible && (
<BuilderText
tagName="h2"
className={`${titleTypography} ${styles.title}`}
style={{ marginBottom: "var(--gap-md)" }}
content={title}
onChange={update('title')}
sectionId={sectionId}
id={titleId}
suffix="title"
/>
)}
{subtitleVisible && (
<BuilderText
tagName="div"
className={`${subtitleTypography} ${styles.subtitle}`}
style={{ marginBottom: "var(--gap-lg)" }}
content={subtitle}
onChange={update('subtitle')}
sectionId={sectionId}
id={subtitleId}
suffix="subtitle"
/>
)}
<div className="buttonWrapperCenter">
{buttonVisible && (
<BuilderButton
label={buttonText}
href={buttonUrl}
openInNewTab={buttonOpenInNewTab}
onOpenInNewTabChange={update('buttonOpenInNewTab')}
isVisible={buttonVisible}
sectionId={sectionId}
className={`btn btn-${buttonStyle} btn-lg`}
iconLeft={buttonIconLeft}
iconRight={buttonIconRight}
onVariantChange={update('buttonStyle')}
linkType={buttonLinkType}
targetDialogId={buttonTargetDialogId}
onIconLeftChange={update('buttonIconLeft')}
onIconRightChange={update('buttonIconRight')}
svgPath={buttonSvgPath}
onSvgPathChange={update('buttonSvgPath')}
svgViewBox={buttonSvgViewBox}
onSvgViewBoxChange={update('buttonSvgViewBox')}
svgStrokeWidth={buttonSvgStrokeWidth}
onSvgStrokeWidthChange={update('buttonSvgStrokeWidth')}
id={buttonId}
suffix="button"
/>
)}
{secondaryButtonVisible && (
<BuilderButton
label={secondaryButtonText}
href={secondaryButtonUrl}
openInNewTab={secondaryButtonOpenInNewTab}
onOpenInNewTabChange={update('secondaryButtonOpenInNewTab')}
isVisible={secondaryButtonVisible}
sectionId={sectionId}
className={`btn btn-${secondaryButtonStyle} btn-lg`}
onVariantChange={update('secondaryButtonStyle')}
linkType={secondaryButtonLinkType}
targetDialogId={secondaryButtonTargetDialogId}
iconLeft={secondaryButtonIconLeft}
iconRight={secondaryButtonIconRight}
onIconLeftChange={update('secondaryButtonIconLeft')}
onIconRightChange={update('secondaryButtonIconRight')}
svgPath={secondaryButtonSvgPath}
onSvgPathChange={update('secondaryButtonSvgPath')}
svgViewBox={secondaryButtonSvgViewBox}
onSvgViewBoxChange={update('secondaryButtonSvgViewBox')}
svgStrokeWidth={secondaryButtonSvgStrokeWidth}
onSvgStrokeWidthChange={update('secondaryButtonSvgStrokeWidth')}
id={secondaryButtonId}
suffix="secondary-button"
/>
)}
</div>
</div>
</div>
</BuilderSection>
);
}